// Lógica del editor de overrides de un client (ClientOverridesModal), sin React.
//
// El backend reemplaza `config_overrides` ENTERO con lo que manda el editor. Por
// eso el objeto a guardar se arma sobre lo que el client ya tiene: las filas del
// editor ponen o sacan SUS claves, y cualquier otra clave guardada (una que esta
// versión del portal no muestra) viaja tal cual. Reconstruirlo solo con las filas
// visibles borraba en silencio todo lo demás.
//
// Shape espejo de ClientConfigOverridesSchema (libs/common/client-config.ts).

import type { ClientConfigOverrides, PartnerDetail } from '@/lib/api';

/** Overrides guardados del client: pueden traer claves que el editor no conoce. */
export type SavedOverrides = ClientConfigOverrides | Record<string, unknown>;

export type FieldKind = 'boolean' | 'number' | 'text';
export type OverrideKey = keyof ClientConfigOverrides;

export interface OverrideField {
  key: OverrideKey;
  label: string;
  hint?: string;
  kind: FieldKind;
  danger: boolean; // requiere danger_zone para cambiarse
  unit?: string;
  min?: number;
  max?: number;
  /** Número: vacío = sin override (en vez de error). */
  emptyMeansUnset?: boolean;
  /** Cómo se muestra la ausencia de valor (partner sin valor, override vacío). */
  emptyLabel?: string;
  /** Punto de ayuda propio de la fila (`data-help`). */
  helpId?: string;
  partnerValue: (p: PartnerDetail) => boolean | number | string | null;
}

// Fuente única de verdad de los campos overrideables del modal. El orden es el
// orden de las filas.
export const OVERRIDE_FIELDS: OverrideField[] = [
  {
    key: 'require_email_verification',
    label: 'Verificación de email obligatoria',
    hint: 'Si está activo, el login rechaza usuarios con email no verificado.',
    kind: 'boolean',
    danger: true,
    partnerValue: (p) => p.require_email_verification,
  },
  {
    key: 'plans_enabled',
    label: 'Mostrar planes / suscripción',
    hint: 'Habilita el circuito comercial: selección de plan en signup y checkout.',
    kind: 'boolean',
    danger: true,
    partnerValue: (p) => p.features?.['plans_enabled'] === true,
  },
  {
    key: 'subscribe_hint_max_age_seconds',
    label: 'Vigencia del enlace de la plataforma',
    hint:
      'Cuánto tiempo sirve el enlace que manda la plataforma para entrar a suscripciones y ' +
      'autogestión, en segundos (30 – 86.400). Vacío = sin límite.',
    kind: 'number',
    unit: 's',
    min: 30,
    max: 86_400,
    emptyMeansUnset: true,
    emptyLabel: 'Sin límite',
    danger: true,
    helpId: 'partner.clients.overrides.link-max-age',
    // No hay valor a nivel partner: sin override, el enlace no vence por tiempo.
    partnerValue: () => null,
  },
  {
    key: 'access_token_ttl_seconds',
    label: 'Access token TTL',
    hint: 'Vida del access token, en segundos (60 – 86.400).',
    kind: 'number',
    unit: 's',
    min: 60,
    max: 86_400,
    danger: false,
    partnerValue: (p) => p.access_token_ttl_seconds,
  },
  {
    key: 'refresh_token_ttl_seconds',
    label: 'Refresh token TTL',
    hint: 'Vida del refresh token, en segundos (3.600 – 31.536.000).',
    kind: 'number',
    unit: 's',
    min: 3_600,
    max: 31_536_000,
    danger: false,
    partnerValue: (p) => p.refresh_token_ttl_seconds,
  },
  {
    key: 'entitlements_cache_ttl_seconds',
    label: 'Entitlement cache TTL',
    hint: 'TTL del cache de entitlements. Se respeta en paths con contexto de client (/me/*); el path partner-API usa el valor del partner.',
    kind: 'number',
    unit: 's',
    min: 0,
    max: 86_400,
    danger: false,
    partnerValue: (p) => p.entitlements_cache_ttl_seconds,
  },
  {
    key: 'webhook_url',
    label: 'Webhook URL',
    hint: 'Destino de webhooks. Se respeta en eventos con contexto de client (device.authorized); el resto usa el del partner. Vacío = este client no emite webhooks.',
    kind: 'text',
    danger: false,
    emptyLabel: '(sin webhook)',
    partnerValue: (p) => p.webhook_url,
  },
];

const EDITED_KEYS: ReadonlySet<string> = new Set(OVERRIDE_FIELDS.map((f) => f.key));

export interface RowState {
  enabled: boolean;
  value: string | boolean;
}

export type Rows = Record<OverrideKey, RowState>;

// ---- formato -------------------------------------------------------------

export function humanSeconds(n: number): string {
  if (n === 0) return '0 s';
  if (n % 86_400 === 0) return `${n} s (${n / 86_400} d)`;
  if (n % 3_600 === 0) return `${n} s (${n / 3_600} h)`;
  if (n % 60 === 0) return `${n} s (${n / 60} min)`;
  return `${n} s`;
}

export function displayValue(f: OverrideField, v: boolean | number | string | null): string {
  if (v === null || v === undefined || v === '') return f.emptyLabel ?? '—';
  if (f.kind === 'boolean') return v ? 'Sí' : 'No';
  if (f.kind === 'number') return humanSeconds(Number(v));
  return String(v);
}

// ---- estado del editor ---------------------------------------------------

/**
 * Valor inicial de cada fila a partir del override guardado del client (o, si
 * no hay override, del valor del partner como punto de partida editable).
 */
export function initialRows(
  saved: SavedOverrides | null | undefined,
  partner: PartnerDetail,
): Rows {
  const ov = (saved ?? {}) as Record<string, unknown>;
  const out = {} as Rows;
  for (const f of OVERRIDE_FIELDS) {
    const has = Object.prototype.hasOwnProperty.call(ov, f.key);
    const partnerVal = f.partnerValue(partner);
    const value: string | boolean =
      f.kind === 'boolean'
        ? has
          ? Boolean(ov[f.key])
          : Boolean(partnerVal)
        : // number / text (webhook_url: override null → cadena vacía = sin webhook)
          String((has ? ov[f.key] : partnerVal) ?? '');
    out[f.key] = { enabled: has, value };
  }
  return out;
}

/** Claves guardadas que el editor no muestra (se conservan al guardar). */
export function hiddenOverrideKeys(
  saved: SavedOverrides | null | undefined,
): string[] {
  return Object.keys(saved ?? {}).filter((k) => !EDITED_KEYS.has(k));
}

/**
 * Objeto de overrides a guardar: lo guardado SIN las claves del editor, más las
 * filas con el override activo. Devuelve también los errores por campo.
 */
export function buildOverrides(
  rows: Rows,
  saved: SavedOverrides | null | undefined,
): {
  overrides: ClientConfigOverrides;
  errors: Partial<Record<OverrideKey, string>>;
} {
  const overrides: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(saved ?? {})) {
    if (!EDITED_KEYS.has(k)) overrides[k] = v;
  }
  const errors: Partial<Record<OverrideKey, string>> = {};
  for (const f of OVERRIDE_FIELDS) {
    const st = rows[f.key];
    if (!st.enabled) continue;
    if (f.kind === 'boolean') {
      overrides[f.key] = Boolean(st.value);
    } else if (f.kind === 'number') {
      const raw = String(st.value).trim();
      const n = Number(raw);
      if (raw === '' && f.emptyMeansUnset) {
        // Vacío = sin override: la clave no se manda.
      } else if (raw === '' || !Number.isInteger(n)) {
        errors[f.key] = 'Ingresá un entero válido.';
      } else if (f.min !== undefined && n < f.min) {
        errors[f.key] = `Mínimo ${f.min}.`;
      } else if (f.max !== undefined && n > f.max) {
        errors[f.key] = `Máximo ${f.max}.`;
      } else {
        overrides[f.key] = n;
      }
    } else {
      // text: webhook_url. Vacío = null (sin webhook). Si no, validar URL.
      const raw = String(st.value).trim();
      if (raw === '') {
        overrides[f.key] = null;
      } else {
        try {
          // eslint-disable-next-line no-new
          new URL(raw);
          overrides[f.key] = raw;
        } catch {
          errors[f.key] = 'URL inválida.';
        }
      }
    }
  }
  return { overrides: overrides as ClientConfigOverrides, errors };
}

function canonical(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonical);
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.keys(v as Record<string, unknown>)
        .sort()
        .map((k) => [k, canonical((v as Record<string, unknown>)[k])]),
    );
  }
  return v;
}

/** ¿Mismo set de overrides? Sin importar el orden de las claves. */
export function sameOverrides(a: unknown, b: unknown): boolean {
  return JSON.stringify(canonical(a ?? {})) === JSON.stringify(canonical(b ?? {}));
}
